import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { maskTime, parse12 } from '../lib/userHours.js';
import { personalTimeFormat } from '../lib/timePrefs.js';

// Custom-on-desktop / native-on-mobile date & time fields (#7, #16), mirroring the
// calendar module's `PK` picker (custom panel on desktop, native <input type=date|time>
// when the pointer is coarse). One reusable pair the whole EHR can adopt in place of raw
// <input type="date"> and bespoke calendars.

function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse)');
    const on = () => setCoarse(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return coarse;
}

function useClickOutside(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return ref;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Parse 'YYYY-MM-DD' as a LOCAL date (no UTC shift); invalid → null. */
function parseISO(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}
function toISO(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function fmtDisplay(d: Date): string {
  // MM/DD/YYYY — matches the V22 demographics + calendar mockups.
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()}`;
}
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
/** Parse a typed 'MM/DD/YYYY' into a local Date; rejects overflow (e.g. 02/31). */
function parseMDY(v: string): Date | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v.trim());
  if (!m) return null;
  const mm = Number(m[1]);
  const dd = Number(m[2]);
  const yy = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const d = new Date(yy, mm - 1, dd);
  if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}
/** Progressive MM/DD/YYYY mask as the user types (digits only). */
function maskMDY(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

export function DateField({
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  min,
  max,
  disabled,
  id,
  ariaLabel,
  onInvalid,
}: {
  /** ISO 'YYYY-MM-DD' (or ''). */
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** ISO 'YYYY-MM-DD' minimum selectable date. */
  min?: string;
  /** ehr#571: ISO 'YYYY-MM-DD' maximum selectable date (later days are greyed, typed ones revert). */
  max?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  /**
   * ehr#708 (DISCHARGE_SUMMARY_NOTE V5): told when a typed date can't be read (it goes back to the
   * saved one), so the screen can say how to type it.
   */
  onInvalid?: () => void;
}) {
  const coarse = useCoarsePointer();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The calendar floats (position: fixed) so a modal's scroll area never clips it (ehr#60,
  // the Hours window's Time Off dates). Below the field, or above when there isn't room
  // (#22), always kept inside the viewport.
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const selected = parseISO(value);
  const minDate = min ? parseISO(min) : null;
  const maxDate = max ? parseISO(max) : null;
  // Which month the grid shows; follows the selected date, else today.
  const [cursor, setCursor] = useState<Date>(() => selected ?? new Date());
  // #57 - the footer year pill opens a scrollable year menu (jump to any year),
  // mirroring the V22 demographics mockup's picker.
  const [yrOpen, setYrOpen] = useState(false);
  // #57 - the trigger is now a typeable MM/DD/YYYY input. `text` mirrors `value`
  // but the user edits it freely; only a complete, valid date commits.
  const [text, setText] = useState(() => (selected ? fmtDisplay(selected) : ''));
  useEffect(() => {
    // Resync the input when the value changes from outside, unless the user is typing.
    if (inputRef.current && document.activeElement === inputRef.current) return;
    setText(selected ? fmtDisplay(selected) : '');
  }, [value]);
  useEffect(() => {
    if (open) {
      if (selected) setCursor(new Date(selected.getFullYear(), selected.getMonth(), 1));
      setYrOpen(false);
    }
    // Only re-sync the visible month/view when the panel opens.
  }, [open]);
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const t = inputRef.current?.parentElement ?? inputRef.current;
    const p = panelRef.current;
    if (!t || !p) return;
    const rect = t.getBoundingClientRect();
    const panelH = p.offsetHeight;
    const panelW = p.offsetWidth;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom;
    // Flip up only when it won't fit below but does fit better above.
    const up = spaceBelow < panelH + margin + 4 && rect.top > spaceBelow;
    const top = up
      ? Math.max(margin, rect.top - panelH - 4)
      : Math.max(margin, Math.min(rect.bottom + 4, vh - panelH - margin));
    const left = Math.max(margin, Math.min(rect.left, vw - panelW - margin));
    setPos({ position: 'fixed', top, left, bottom: 'auto', right: 'auto', zIndex: 1300 });
  }, [open, cursor, yrOpen]);
  useEffect(() => {
    if (!open) return;
    // The page or the modal scrolled under the floating calendar: close it.
    const close = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, ref]);

  const atOrAfterMin = (d: Date) =>
    (!minDate || d >= new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) &&
    (!maxDate || d <= new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate()));
  // Commit the typed text on blur/Enter: valid date -> onChange; empty -> clear;
  // anything else reverts to the last committed value.
  const commitText = (raw: string) => {
    const t = raw.trim();
    if (!t) {
      onChange('');
      return;
    }
    const d = parseMDY(t);
    if (d && atOrAfterMin(d)) {
      onChange(toISO(d));
      setText(fmtDisplay(d));
      setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
    } else {
      setText(selected ? fmtDisplay(selected) : '');
      onInvalid?.();
    }
  };

  if (coarse) {
    return (
      <input
        id={id}
        type="date"
        className="dtf-native"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const today = new Date();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++)
    cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const isDisabled = (d: Date) => !atOrAfterMin(d);
  const shiftMonth = (delta: number) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  // #57 - full, scrollable year range so any birth year is one scroll away. The
  // menu centers on the current year, so it looks like the mockup's short window.
  const minYear = minDate ? minDate.getFullYear() : 1900;
  const maxYear = maxDate
    ? Math.max(maxDate.getFullYear(), cursor.getFullYear())
    : Math.max(today.getFullYear() + 20, cursor.getFullYear());
  const years: number[] = [];
  for (let y = minYear; y <= maxYear; y++) years.push(y);

  return (
    <div
      className={`dtf${open ? ' dtf-open' : ''}`}
      ref={ref}
      onKeyDown={(e) => {
        // ehr#571: Esc closes an open calendar only, not the window or menu around it
        if (e.key !== 'Escape' || !open) return;
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setOpen(false);
      }}
    >
      <div className="dtf-trigger-wrap">
        <input
          id={id}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={`dtf-input${selected ? '' : ' empty'}`}
          placeholder={placeholder}
          value={text}
          disabled={disabled}
          aria-label={ariaLabel}
          onChange={(e) => {
            const masked = maskMDY(e.target.value);
            setText(masked);
            const d = parseMDY(masked);
            if (d && atOrAfterMin(d)) {
              onChange(toISO(d));
              setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
            } else if (masked === '') {
              onChange('');
            }
          }}
          onBlur={(e) => commitText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commitText((e.target as HTMLInputElement).value);
              setOpen(false);
            }
          }}
        />
        <button
          type="button"
          className="dtf-cal-btn"
          aria-label="Open calendar"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      {open && (
        <div
          className="dtf-panel"
          role="dialog"
          aria-label="Choose a date"
          ref={panelRef}
          style={pos ?? { visibility: 'hidden' }}
        >
          <div className="dtf-head">
            <button
              type="button"
              className="dtf-nav"
              aria-label="Previous month"
              onClick={() => {
                setYrOpen(false);
                shiftMonth(-1);
              }}
            >
              ‹
            </button>
            <span className="dtf-title">
              {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
            </span>
            <button
              type="button"
              className="dtf-nav"
              aria-label="Next month"
              onClick={() => {
                setYrOpen(false);
                shiftMonth(1);
              }}
            >
              ›
            </button>
          </div>
          <div className="dtf-grid dtf-wk">
            {WEEKDAYS.map((w) => (
              <span key={w} className="dtf-wkday">
                {w}
              </span>
            ))}
          </div>
          <div className="dtf-grid">
            {cells.map((d, i) =>
              d === null ? (
                <span key={`p${i}`} className="dtf-cell dtf-empty" />
              ) : (
                <button
                  key={toISO(d)}
                  type="button"
                  className={`dtf-cell${selected && sameDay(d, selected) ? ' sel' : ''}${
                    sameDay(d, today) ? ' today' : ''
                  }`}
                  disabled={isDisabled(d)}
                  onClick={() => {
                    onChange(toISO(d));
                    setText(fmtDisplay(d));
                    setOpen(false);
                  }}
                >
                  {d.getDate()}
                </button>
              ),
            )}
          </div>
          <div className="dtf-foot">
            {atOrAfterMin(new Date(today.getFullYear(), today.getMonth(), today.getDate())) ? (
              <button
                type="button"
                className="dtf-quick"
                onClick={() => {
                  const n = new Date();
                  onChange(toISO(n));
                  setText(fmtDisplay(n));
                  setOpen(false);
                }}
              >
                Today
              </button>
            ) : (
              <span />
            )}
            <div className={`dtf-yr${yrOpen ? ' dtf-yr-open' : ''}`}>
              <button
                type="button"
                className="dtf-yr-btn"
                aria-label="Jump to year"
                aria-expanded={yrOpen}
                onClick={() => setYrOpen((o) => !o)}
              >
                <span>{cursor.getFullYear()}</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {yrOpen && (
                <div className="dtf-yr-menu" role="listbox" aria-label="Year">
                  {years.map((y) => (
                    <button
                      key={y}
                      type="button"
                      role="option"
                      aria-selected={y === cursor.getFullYear()}
                      ref={
                        y === cursor.getFullYear()
                          ? (el) => {
                              // Scroll only the year menu (scrollIntoView would also scroll
                              // the modal behind the floating calendar, which closes it).
                              const list = el?.parentElement;
                              if (el && list)
                                list.scrollTop +=
                                  el.getBoundingClientRect().top -
                                  list.getBoundingClientRect().top -
                                  (list.clientHeight - el.offsetHeight) / 2;
                            }
                          : undefined
                      }
                      className={`dtf-yr-opt${y === cursor.getFullYear() ? ' on' : ''}`}
                      onClick={() => {
                        setCursor((c) => new Date(y, c.getMonth(), 1));
                        setYrOpen(false);
                      }}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selected ? (
              <button
                type="button"
                className="dtf-quick"
                onClick={() => {
                  onChange('');
                  setText('');
                  setOpen(false);
                }}
              >
                Clear
              </button>
            ) : (
              <span />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Format 'HH:MM' (24h) → '9:05 AM' for the desktop trigger + list, or '09:05' for a person on
 * 24-hour (ehr#386, Chase 10-14: the progress note's time picker was still 12-hour).
 */
function fmtTime(v: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v);
  if (!m) return v;
  if (personalTimeFormat() === '24h') return `${m[1]!.padStart(2, '0')}:${m[2]}`;
  let h = Number(m[1]);
  const ampm = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ampm}`;
}

/**
 * A time field you can TYPE in ("9", "930p", "14:00", "9:30 am") or pick from the clock
 * (ehr#60: every time box takes both). Typed text commits on blur / Enter; text that isn't a
 * time is flagged and not saved. ehr#211: the clock is the Calendar's appointment time
 * picker: hour, minute and AM / PM columns, each starting at the current value. A click sets
 * that part and the panel stays open, so 10 → 30 → AM is three clicks. It floats
 * (position: fixed) so a modal or a scrolling card never clips it. Coarse pointers get the
 * native time input.
 */
export function TimeField({
  value,
  onChange,
  step = 1,
  placeholder = 'Select a time',
  disabled,
  id,
  ariaLabel,
  className,
  onInvalid,
}: {
  /** 'HH:MM' 24-hour, or ''. */
  value: string;
  onChange: (v: string) => void;
  /** Minutes between choices in the minute column (every minute by default, like the Calendar). */
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  /** Extra class on the wrapper (e.g. a compact variant). */
  className?: string;
  /**
   * ehr#708 (DISCHARGE_SUMMARY_NOTE V5): given, a time that can't be read goes back to the saved
   * one (instead of staying in the box in red) and this is told, so the screen can say how to type
   * it.
   */
  onInvalid?: () => void;
}) {
  const coarse = useCoarsePointer();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(value ? fmtTime(value) : '');
  const [bad, setBad] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);

  // Follow outside changes, unless the user is typing in this box.
  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) return;
    setText(value ? fmtTime(value) : '');
    setBad(false);
  }, [value]);

  useLayoutEffect(() => {
    if (!open) return setPos(null);
    const box = ref.current?.querySelector('.dtf-combo');
    if (!box) return;
    const r = box.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const up = below < 300 && r.top > below;
    setPos({
      position: 'fixed',
      left: r.left,
      zIndex: 1300,
      ...(up ? { top: 'auto', bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
    });
    const close = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, ref]);

  if (coarse) {
    return (
      <input
        id={id}
        type="time"
        className="dtf-native"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // ehr#211: the wheel's parts. Each column starts at the current value (an empty field
  // starts the hour column at 12 and lands on 9:00 AM at the first click).
  type Parts = { h12: number; mi: number; ap: 'AM' | 'PM' };
  const tm = /^(\d{1,2}):(\d{2})$/.exec(value);
  const parts: Parts | null = tm
    ? { h12: Number(tm[1]) % 12 || 12, mi: Number(tm[2]), ap: Number(tm[1]) >= 12 ? 'PM' : 'AM' }
    : null;
  const base: Parts = parts ?? { h12: 12, mi: 0, ap: 'AM' };
  const hourCol = Array.from({ length: 12 }, (_, i) => ((base.h12 - 1 + i) % 12) + 1);
  const allMins: number[] = [];
  for (let m = 0; m < 60; m += Math.max(1, step)) allMins.push(m);
  const at = Math.max(0, allMins.indexOf(base.mi));
  const minuteCol = [...allMins.slice(at), ...allMins.slice(0, at)];
  const ampmCol: ('AM' | 'PM')[] = base.ap === 'PM' ? ['PM', 'AM'] : ['AM', 'PM'];
  // ehr#386: on 24-hour the hour column is 00-23 and there is no AM / PM column, as in the
  // Calendar and RCM
  const h24 = personalTimeFormat() === '24h';
  const cur24 = tm ? Number(tm[1]) : null;
  const hourCol24 = Array.from({ length: 24 }, (_, i) => ((cur24 ?? 0) + i) % 24);
  const setHour24 = (h: number) => {
    const t = `${String(h).padStart(2, '0')}:${String(parts?.mi ?? 0).padStart(2, '0')}`;
    setText(fmtTime(t));
    setBad(false);
    if (t !== value) onChange(t);
  };
  const setPart = (next: Partial<Parts>) => {
    const p = { ...(parts ?? { h12: 9, mi: 0, ap: 'AM' }), ...next };
    const h = (p.h12 % 12) + (p.ap === 'PM' ? 12 : 0);
    const t = `${String(h).padStart(2, '0')}:${String(p.mi).padStart(2, '0')}`;
    setText(fmtTime(t));
    setBad(false);
    if (t !== value) onChange(t);
  };
  const cell = (key: string, label: string, on: boolean, onPick: () => void) => (
    <button
      key={key}
      type="button"
      className={`dtf-wheel-cell${on ? ' on' : ''}`}
      aria-pressed={on}
      // Keep focus in the text box, so a click doesn't blur-commit half-typed text.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPick}
    >
      {label}
    </button>
  );

  const commit = () => {
    const t = text.trim();
    if (!t) {
      setBad(false);
      if (value) onChange('');
      return;
    }
    const p = parse12(t);
    if (!p && onInvalid) {
      setBad(false);
      setText(value ? fmtTime(value) : '');
      onInvalid();
      return;
    }
    if (!p) return setBad(true);
    setBad(false);
    setText(fmtTime(p));
    if (p !== value) onChange(p);
  };

  return (
    <div
      className={`dtf dtf-time${open ? ' dtf-open' : ''}${className ? ` ${className}` : ''}`}
      ref={ref}
    >
      <div className={`dtf-combo${bad ? ' invalid' : ''}${disabled ? ' disabled' : ''}`}>
        <input
          ref={inputRef}
          id={id}
          className={`dtf-time-input${text ? '' : ' empty'}`}
          value={text}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-invalid={bad || undefined}
          title={
            bad
              ? h24
                ? 'Type a time like 14:00 or 0930'
                : 'Type a time like 9:30 AM, 930p or 14:00'
              : undefined
          }
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            // ehr#60 - masked as you type: only a time shape ("9:30 PM") gets in.
            setText(maskTime(e.target.value, text));
            setBad(false);
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              setOpen(false);
            } else if (e.key === 'ArrowDown' && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />
        <button
          type="button"
          className="dtf-time-btn"
          tabIndex={-1}
          aria-label="Pick a time"
          disabled={disabled}
          // Keep focus in the text box, so picking doesn't blur-commit half-typed text.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            // Focus this box, so Escape (and a host modal's Escape guard) sees the open list.
            inputRef.current?.focus({ preventScroll: true });
            setOpen((o) => !o);
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open && (
        <div
          className="dtf-panel dtf-wheel"
          role="dialog"
          aria-label="Pick a time"
          style={pos ?? { visibility: 'hidden' }}
        >
          <div className="dtf-wheel-cols">
            <div className="dtf-wheel-col" aria-label="Hour">
              {h24
                ? hourCol24.map((h) =>
                    cell(`h${h}`, String(h).padStart(2, '0'), cur24 === h, () => setHour24(h)),
                  )
                : hourCol.map((h) =>
                    cell(`h${h}`, String(h).padStart(2, '0'), parts?.h12 === h, () =>
                      setPart({ h12: h }),
                    ),
                  )}
            </div>
            <div className="dtf-wheel-col" aria-label="Minute">
              {minuteCol.map((m) =>
                cell(`m${m}`, String(m).padStart(2, '0'), parts?.mi === m, () =>
                  setPart({ mi: m }),
                ),
              )}
            </div>
            {!h24 && (
              <div className="dtf-wheel-col" aria-label="AM or PM">
                {ampmCol.map((a) => cell(a, a, parts?.ap === a, () => setPart({ ap: a })))}
              </div>
            )}
          </div>
          {value && (
            <div className="dtf-wheel-foot">
              <button
                type="button"
                className="dtf-wheel-clear"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setText('');
                  setBad(false);
                  setOpen(false);
                  onChange('');
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
